import { Box, MenuItem } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useGridStackRenderContext } from '../GridStack/grid-stack-render-context';
import PositionGraph from '../../Graphs/PositionGraph';
import { GRAPH_SIZES } from '../../Graphs/sensor-utils';
import { defaultWidgetTitle } from './widgetTitle';
import { useWidgetHeader } from './WidgetHeaderContext';
import { usePersistedWidgetStyle } from './usePersistedWidgetStyle';
import RawDataWidget from './RawDataWidget';
import LiveNumberWidget from './LiveNumberWidget';
import { GraphNoDataMessage } from '../../Graphs/GraphNoDataMessage';
import { useDashboardProgramRunning, useLiveGraph } from '../LiveGraphStore';

interface PositionWidgetProps {
  id: string;
  style: string;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

const PositionWidget = ({ id, style }: PositionWidgetProps) => {
  const { t } = useTranslation();
  const graph = useLiveGraph('pos');
  const isProgramRunning = useDashboardProgramRunning();
  const { getWidgetContainer } = useGridStackRenderContext();

  const [chartSize, setChartSize] = useState(GRAPH_SIZES.live.size);

  const { widgetStyle, handleSetStyle, styleMenuItems } = usePersistedWidgetStyle(id, style);

  const headerTitle = defaultWidgetTitle('pos', 'all', t) ?? 'pos';

  const menuItems = useMemo(
    () => (
      <>
        <MenuItem onClick={() => handleSetStyle('default')}>
          <Trans i18nKey="positionWidget.menuItem.default">Position Map</Trans>
        </MenuItem>
        {styleMenuItems}
      </>
    ),
    [handleSetStyle, styleMenuItems]
  );

  useWidgetHeader({ title: headerTitle, menuItems }, [headerTitle, menuItems]);

  useEffect(() => {
    const el = getWidgetContainer(id);
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const size = clamp(Math.min(rect.width, rect.height) - 72 /* header + padding */, 180, 900);
      setChartSize(size);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [getWidgetContainer, id]);

  const positionGraphSize = useMemo(() => {
    // Keep the same shape PositionGraph expects (box/title keys),
    // but scale it to the widget's live container size.
    return {
      ...GRAPH_SIZES.live,
      size: chartSize,
      box: { padding: GRAPH_SIZES.live.box.padding },
      title: { fontSize: GRAPH_SIZES.live.title.fontSize },
    };
  }, [chartSize]);

  if (widgetStyle === 'raw') {
    return <RawDataWidget graph={graph} />;
  }

  if (widgetStyle === 'number') {
    return <LiveNumberWidget graph={graph} />;
  }

  return (
    <Box flex="1" overflow="hidden" justifyContent="center" alignItems="center" display="flex" width="100%" height="100%">
      {graph ? <PositionGraph graph={graph} size={positionGraphSize} live={isProgramRunning} /> : <GraphNoDataMessage />}
    </Box>
  );
};

export default PositionWidget;
